import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  styled,
  Slider,
  Paper,
  Avatar,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMoneyBillWave,
  faMapMarkerAlt,
  faBriefcase,
  faChartLine,
  faCircleCheck,
  faCircleExclamation,
  faRobot,
} from '@fortawesome/free-solid-svg-icons'
import { Slide, toast, ToastContainer } from 'react-toastify'
import Api from '../../../../../services/api.service'
import { handlingErrors } from '../../../../../utils'
import { useSelector } from 'react-redux'
import { ROLE_CANDIDAT } from '../../../../../data'
import { faMoneyBill1 } from '@fortawesome/pro-regular-svg-icons'
import { faMoneyBillTrendUp } from '@fortawesome/pro-light-svg-icons'
import { useNavigate } from 'react-router-dom'

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8f9fa',
    '& fieldset': {
      borderColor: '#e0e0e0',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: '#A4ADFF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#A4ADFF',
      borderWidth: '2px',
    },
  },
}))

const ExperienceSlider = styled(Slider)(({ theme }) => ({
  '& .MuiSlider-thumb': {
    backgroundColor: '#A4ADFF',
    border: '2px solid #fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  '& .MuiSlider-track': {
    backgroundColor: '#A4ADFF',
    height: '6px',
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#e0e0e0',
    height: '6px',
  },
}))

const SalaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
  },
}))

export default function SalarySimulationSection() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const userData = useSelector((state) => state.userAuth.user)

  const role = userData?.data?.role
  const isAuthenticated = !!userData
  const isCandidate = isAuthenticated && role === ROLE_CANDIDAT

  const [formData, setFormData] = useState({
    jobTitle: '',
    city: '',
    experience: 3, // Default 3 years experience
  })
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)
  const [salaryData, setSalaryData] = useState(null)
  const [error, setError] = useState('')
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState([])
  const [citySuggestions, setCitySuggestions] = useState([])
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null)
  const [jobDebounceTimer, setJobDebounceTimer] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const [showText, setShowText] = useState(true)

  useEffect(() => {
    // Load candidate data if user is logged in as candidate
    const loadCandidateData = async () => {
      if (isCandidate && userData.data?.id) {
        try {
          const result = await Api.user.detail(userData.data.id)
          const handling = handlingErrors(result)

          if (handling.ok) {
            const candidateData = handling.data

            console.log(candidateData)

            // Update form with candidate data
            setFormData((prev) => ({
              ...prev,
              jobTitle: candidateData.candidat.poste.titre,
              city: candidateData.ville,
              experience:
                calculateExperience(candidateData.candidat.experiences) ||
                prev.experience,
            }))
          }
        } catch (error) {
          console.error('Error loading candidate data:', error)
        }
      } else {
        // Set default city for non-candidates or logged out users
        getDefaultCity()
      }
    }

    loadCandidateData()
    handleCityInputChange()

    // Cleanup debounce timers on unmount
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      if (jobDebounceTimer) {
        clearTimeout(jobDebounceTimer)
      }
    }
  }, [isCandidate, userData?.data?.id])

  const getDefaultCity = () => {
    const savedCity = localStorage.getItem('userCity')
    if (savedCity) {
      setFormData((prev) => ({ ...prev, city: savedCity }))
      return
    }

    // Capital cities mapping for default
    const countryCode = localStorage.getItem('codePays') || 'ch'
    const capitalCities = {
      fr: 'Paris',
      en: 'London',
      de: 'Berlin',
      es: 'Madrid',
      it: 'Rome',
      pt: 'Lisbon',
      be: 'Brussels',
      ch: 'Zurich',
      nl: 'Amsterdam',
      lu: 'Luxembourg',
      ca: 'Ottawa',
      us: 'Washington',
      au: 'Canberra',
    }

    const defaultCity = capitalCities[countryCode] || 'Zurich'
    setFormData((prev) => ({ ...prev, city: defaultCity }))
  }

  const calculateExperience = (experienceData) => {
    if (!experienceData) return 3

    // Handle different experience formats
    if (typeof experienceData === 'number') {
      return experienceData
    }

    if (typeof experienceData === 'string') {
      // Extract number from strings like "3 years", "5+ years", etc.
      const match = experienceData.match(/(\d+)/)
      if (match) {
        const years = parseInt(match[1])
        // Handle "+" in strings like "5+"
        return experienceData.includes('+') ? years + 1 : years
      }
    }

    return 3 // default
  }

  const getPostes = async (searchQuery) => {
    let payload = {
      search: searchQuery,
    }

    payload = Object.entries(payload).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )
    setLoadingJobs(true)

    try {
      let result = await Api.poste.listWithFilter({
        status: 1,
        ...payload,
      })

      const handling = handlingErrors(result)

      if (handling.ok) {
        const res = handling.data
        setLoadingJobs(false)
        return res
      } else {
        setLoadingJobs(false)
        return []
      }
    } catch (error) {
      setLoadingJobs(false)
      return []
    }
  }

  const handleJobChange = (event, newValue) => {
    setSelectedJob(newValue)
    const jobValue = newValue?.value || newValue || ''
    setFormData({
      ...formData,
      jobTitle: jobValue,
    })
  }

  const handleJobTitleChange = async (event, value) => {
    const searchValue = value || event?.target?.value || ''
    setFormData({
      ...formData,
      jobTitle: searchValue,
    })

    // Clear existing timer
    if (jobDebounceTimer) {
      clearTimeout(jobDebounceTimer)
    }

    // Set new timer to search after user stops typing
    const newTimer = setTimeout(async () => {
      if (searchValue && searchValue.length > 2) {
        try {
          const jobs = await getPostes(searchValue)
          const jobSuggestions = jobs.map((job) => ({
            value: job.intitule || job.titre || job.title,
            label: job.intitule || job.titre || job.title,
            t0: job.t0,
            t1: job.t1,
            t2: job.t2,
            t3: job.t3,
            t4: job.t4,
            t5: job.t5,
          }))
          setJobTitleSuggestions(jobSuggestions)
        } catch (error) {
          console.error('Error searching jobs:', error)
          setJobTitleSuggestions([])
        }
      } else {
        setJobTitleSuggestions([])
      }
    }, 500)

    setJobDebounceTimer(newTimer)
  }

  const handleCityChange = (event, newValue) => {
    setFormData({
      ...formData,
      city: newValue || '',
    })
  }

  const handleCityInputChange = async (event, value) => {
    setLoadingCities(true)
    try {
      // Get country code and language from localStorage
      /*  const countryCode = localStorage.getItem('codePays') || 'ch'
          const language = localStorage.getItem('lang') || 'ch' */

      // Use a city search API with dynamic country and language
      const response = await Api.countriesCities.getCitiesByCountry(
        'Switzerland'
      )
      /*  const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
              value
            )}&countrycodes=${countryCode}&limit=5&accept-language=${language}`,
            {
              headers: {
                'User-Agent': 'jobertech-app',
              },
            }
          ) */
      //const data = await response.json()

      /*  const $cities = data.filter(
            (item) => item.addresstype === 'city' || item.addresstype === 'town'
          ) */

      /*  const cities = $cities.map((item) => {
            // Extract only city name
            const cityName = item.name || item.display_name.split(',')[0]
            return cityName
          }) */

      setCitySuggestions(response.data.data)
    } catch (error) {
      console.error('Error searching cities:', error)
      setCitySuggestions([])
    } finally {
      setLoadingCities(false)
    }
  }

  const handleExperienceChange = (event, newValue) => {
    setFormData({
      ...formData,
      experience: newValue,
    })
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const getBaseSalary = (jobTitle) => {
    const salaries = {
      developer: 45000,
      designer: 38000,
      manager: 55000,
      analyst: 42000,
      engineer: 48000,
      consultant: 50000,
      marketing: 40000,
      sales: 35000,
      hr: 37000,
      finance: 52000,
      data: 46000,
      product: 53000,
    }

    const title = jobTitle.toLowerCase()
    for (const [key, salary] of Object.entries(salaries)) {
      if (title.includes(key)) return salary
    }
    return 40000 // default
  }

  const getExperienceMultiplier = (years) => {
    if (years < 1) return 0.7 // Junior
    if (years < 3) return 0.85 // Junior-Mid
    if (years < 5) return 1.0 // Mid-level
    if (years < 8) return 1.25 // Senior
    if (years < 12) return 1.5 // Lead
    return 1.8 // Executive/Principal
  }

  const getCurrency = (city) => {
    return 'CHF'
  }

  const getLocationMultiplier = (city) => {
    const multipliers = {
      'new york': 1.4,
      'san francisco': 1.5,
      chicago: 1.1,
      washington: 1.2,
      paris: 1.2,
      lyon: 1.0,
      marseille: 0.9,
      lille: 0.95,
      london: 1.3,
      manchester: 1.0,
      birmingham: 0.9,
      berlin: 1.1,
      munich: 1.2,
      frankfurt: 1.15,
      hamburg: 1.05,
      amsterdam: 1.25,
      rotterdam: 1.1,
      utrecht: 1.05,
      brussels: 1.1,
      antwerp: 1.0,
      gent: 0.95,
      barcelona: 1.0,
      madrid: 1.1,
      valencia: 0.9,
      rome: 1.0,
      milan: 1.1,
      turin: 0.95,
      lisbon: 0.9,
      porto: 0.8,
      coimbra: 0.75,
    }

    const cityLower = city.toLowerCase()
    for (const [cityKey, multiplier] of Object.entries(multipliers)) {
      if (cityLower.includes(cityKey)) return multiplier
    }
    return 1.0 // default multiplier
  }

  //alert(t('errorMessage.aiSuggestionRequired'))

  const handleSuggestion = async () => {
    if (!formData.jobTitle || !formData.city) {
      setError(t('errorMessage.aiSuggestionRequired'))
      return
    }

    setLoadingSuggestion(true)
    setError('')


    // Get country from city or default to FR
    const country = getCountryFromCity(formData.city)
    const currency = getCurrency(formData.city)

    console.log(selectedJob, formData.experience, selectedJob)

    if (selectedJob && formData.experience < 4 && selectedJob.t0) {
      const salaryRange = parseSalaryRange(selectedJob.t0)
      setSalaryData({
        min: salaryRange.min,
        average: salaryRange.average,
        max: salaryRange.max,
        currency: currency,
        aiGenerated: true,
      })
      setShowText(false)
      setLoadingSuggestion(false)
      return
    }

    if (selectedJob && formData.experience > 3 && formData.experience < 6 && selectedJob.t1) {
      const salaryRange = parseSalaryRange(selectedJob.t1)
      setSalaryData({
        min: salaryRange.min,
        average: salaryRange.average,
        max: salaryRange.max,
        currency: currency,
        aiGenerated: true,
      })
      setShowText(false)
      setLoadingSuggestion(false)
      return
    }

    if (selectedJob && formData.experience > 5 && formData.experience < 11 && selectedJob.t2) {
      const salaryRange = parseSalaryRange(selectedJob.t2)
      setSalaryData({
        min: salaryRange.min,
        average: salaryRange.average,
        max: salaryRange.max,
        currency: currency,
        aiGenerated: true,
      })
      setShowText(false)
      setLoadingSuggestion(false)
      return
    }

    if (selectedJob && formData.experience > 10 && formData.experience < 16 && selectedJob.t3) {
      const salaryRange = parseSalaryRange(selectedJob.t3)
      setSalaryData({
        min: salaryRange.min,
        average: salaryRange.average,
        max: salaryRange.max,
        currency: currency,
        aiGenerated: true,
      })
      setShowText(false)
      setLoadingSuggestion(false)
      return
    }

    if (selectedJob && formData.experience > 15 && formData.experience < 20 && selectedJob.t4) {
      const salaryRange = parseSalaryRange(selectedJob.t4)
      setSalaryData({
        min: salaryRange.min,
        average: salaryRange.average,
        max: salaryRange.max,
        currency: currency,
        aiGenerated: true,
      })
      setShowText(false)
      setLoadingSuggestion(false)
      return
    }

    if (selectedJob && formData.experience > 19 && selectedJob.t5) {
      const salaryRange = parseSalaryRange(selectedJob.t5)
      setSalaryData({
        min: salaryRange.min,
        average: salaryRange.average,
        max: salaryRange.max,
        currency: currency,
        aiGenerated: true,
      })
      setShowText(false)
      setLoadingSuggestion(false)
      return
    }

    try {

      const result = await Api.besoinEntreprise.suggestionIA(
        formData.jobTitle,
        formData.city,
        country,
        i18n.language,
        currency,
        formData.experience
      )

      const handling = handlingErrors(result)

      if (handling.status === 401) {
        setError('Session expired. Please log in again.')
        return
      }

      if (handling.ok) {
        const aiData = handling.data.data

        // Use AI-generated salary range if available, otherwise fall back to calculation
        if (aiData.salary_range) {
          // Parse AI salary range (format might be like "40000-60000" or "€40k-€60k")
          const salaryRange = parseSalaryRange(aiData.salary_range)
          setSalaryData({
            min: salaryRange.min,
            average: salaryRange.average,
            max: salaryRange.max,
            currency: currency,
            aiGenerated: true,
          })
        } else {
          // Set loading to false if AI doesn't provide salary
          setLoadingSuggestion(false)
        }
        setShowText(true)

        // Show success toast
        toast.success(t('salarySimulation.successMessage'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
      } else {
        console.log('❌ Error - status:', handling.status)
        toast.error(t('salarySimulation.errorMessage'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
      }
    } catch (error) {
      console.error('💥 Caught error:', error)
      toast.error(t('salarySimulation.networkError'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      })
    } finally {
      setLoadingSuggestion(false)
    }
  }

  const getCountryFromCity = (city) => {
    return 'Suisse' // default
  }

  const parseSalaryRange = (salaryRange) => {
    // Parse various salary range formats including Swiss format
    const range = salaryRange.toString()

    // Remove currency symbols, 'k' for thousands, and apostrophes for Swiss format
    const cleanRange = range.replace(/[€$£¥kK']/g, '').replace(/\s/g, '')

    // Try to extract numbers
    const matches = cleanRange.match(/(\d+)/g)

    if (matches && matches.length >= 2) {
      // Handle Swiss format with apostrophes (e.g., "90'000-130'000 CHF")
      const hasApostrophes = range.includes("'")
      const multiplier = range.includes('k') || range.includes('K') ? 1000 : 1

      const min = parseInt(matches[0]) * multiplier
      const max = parseInt(matches[1]) * multiplier
      const average = Math.round((min + max) / 2)

      return { min, average, max }
    } else if (matches && matches.length === 1) {
      const hasApostrophes = range.includes("'")
      const multiplier = range.includes('k') || range.includes('K') ? 1000 : 1

      const salary = parseInt(matches[0]) * multiplier
      return {
        min: Math.round(salary * 0.8),
        average: salary,
        max: Math.round(salary * 1.2),
      }
    }

    // Fallback to calculation
    const baseSalary = getBaseSalary(formData.jobTitle)
    const locationMultiplier = getLocationMultiplier(formData.city)
    const experienceMultiplier = getExperienceMultiplier(formData.experience)
    const estimatedSalary = Math.round(
      baseSalary * locationMultiplier * experienceMultiplier
    )

    return {
      min: Math.round(estimatedSalary * 0.8),
      average: estimatedSalary,
      max: Math.round(estimatedSalary * 1.2),
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8, mt: 8 }}>
      <Box textAlign="center" mb={8}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: '#A4ADFF',
            mx: 'auto',
            mb: 3,
            fontSize: '40px',
          }}
        >
          <FontAwesomeIcon icon={faMoneyBillTrendUp} />
        </Avatar>
        <Typography variant="h2" component="h2" gutterBottom fontWeight="bold">
          {t('salarySimulation.title')}
        </Typography>
        <Typography variant="h6" paragraph mx="auto">
          {t('salarySimulation.description')}
        </Typography>
      </Box>

      <Card elevation={8} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 3, md: 6 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <FontAwesomeIcon
                    icon={faBriefcase}
                    style={{ color: '#A4ADFF' }}
                  />
                  {t('salarySimulation.jobTitle')}
                </Typography>
                <StyledAutocomplete
                  freeSolo
                  options={jobTitleSuggestions}
                  value={formData.jobTitle}
                  onChange={handleJobChange}
                  onInputChange={handleJobTitleChange}
                  loading={loadingJobs}
                  noOptionsText={
                    loadingJobs
                      ? t('home.loading', 'Loading...')
                      : jobTitleSuggestions.length === 0 &&
                        formData.jobTitle.length > 2
                        ? t('home.noJobsFound', 'No jobs found')
                        : ''
                  }
                  renderOption={(props, option) => (
                    <Box
                      {...props}
                      sx={{ display: 'flex', alignItems: 'center', py: 1 }}
                    >
                      <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
                        {option.label}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="e.g. Software Developer"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: loadingJobs && (
                          <div
                            style={{
                              padding: '0 8px',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#666',
                            }}
                          >
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #666',
                                borderTop: 'none',
                                borderLeft: 'none',
                                animation: 'spin 1s linear infinite',
                                borderRadius: '50%',
                                borderRight: '2px solid transparent',
                                borderColor: '#666 transparent',
                                borderStyle: 'solid',
                              }}
                            ></div>
                          </div>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <FontAwesomeIcon
                    icon={faMapMarkerAlt}
                    style={{ color: '#A4ADFF' }}
                  />
                  {t('salarySimulation.city')}
                </Typography>
                <StyledAutocomplete
                  freeSolo
                  options={citySuggestions}
                  value={formData.city}
                  onChange={handleCityChange}
                  onInputChange={handleCityInputChange}
                  loading={loadingCities}
                  noOptionsText={
                    loadingCities
                      ? t('home.loading', 'Loading...')
                      : citySuggestions.length === 0 && formData.city.length > 2
                        ? t('home.noCitiesFound', 'No cities found')
                        : ''
                  }
                  renderOption={(props, option) => (
                    <Box
                      {...props}
                      sx={{ display: 'flex', alignItems: 'center', py: 1 }}
                    >
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        style={{
                          color: '#667eea',
                          marginRight: '8px',
                          marginLeft: '0px',
                          fontSize: '14px',
                        }}
                      />
                      <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
                        {option}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="e.g. Paris"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: loadingCities && (
                          <div
                            style={{
                              padding: '0 8px',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#666',
                            }}
                          >
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #666',
                                borderTop: 'none',
                                borderLeft: 'none',
                                animation: 'spin 1s linear infinite',
                                borderRadius: '50%',
                                borderRight: '2px solid transparent',
                                borderColor: '#666 transparent',
                                borderStyle: 'solid',
                              }}
                            ></div>
                          </div>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box mb={4}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <FontAwesomeIcon
                    icon={faChartLine}
                    style={{ color: '#A4ADFF' }}
                  />
                  {t('salarySimulation.experience', 'Years of Experience')}:{' '}
                  <Typography
                    component="span"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    {formData.experience} {t('salarySimulation.years', 'years')}
                  </Typography>
                </Typography>
                <ExperienceSlider
                  value={formData.experience}
                  onChange={handleExperienceChange}
                  min={0}
                  max={20}
                  step={1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 15, label: '15' },
                    { value: 20, label: '20+' },
                  ]}
                  valueLabelDisplay="off"
                  sx={{
                    mt: 2,
                    '& .MuiSlider-mark': {
                      color: '#A4ADFF',
                    },
                    '& .MuiSlider-markActive': {
                      color: '#A4ADFF',
                    },
                    '& .MuiSlider-markLabel': {
                      color: '#666',
                      fontSize: '14px',
                    },
                    '& .MuiSlider-markLabelActive': {
                      color: '#A4ADFF',
                      fontWeight: 'bold',
                    },
                    '& .MuiSlider-valueLabel': {
                      color: '#A4ADFF',
                      fontWeight: 'bold',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box textAlign="center" mt={6}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSuggestion}
              disabled={loadingSuggestion}
              sx={{
                minWidth: 250,
                py: 2,
                px: 6,
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '50px',
                textTransform: 'none',
                color: '#fff',

                background: 'linear-gradient(135deg, #A4ADFF 0%, #7F8CFF 100%)',

                boxShadow: '0 4px 14px rgba(164, 173, 255, 0.4)',

                transition: 'all 0.3s ease',

                '&:hover': {
                  background:
                    'linear-gradient(135deg, #7F8CFF 0%, #5C6CFA 100%)',
                  boxShadow: '0 6px 20px rgba(164, 173, 255, 0.6)',
                  transform: 'translateY(-2px)',
                },

                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 3px 10px rgba(164, 173, 255, 0.4)',
                },

                '&:disabled': {
                  background: '#d3d6ff',
                  color: '#888',
                  boxShadow: 'none',
                },
              }}
            >
              {loadingSuggestion ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 2, color: '#fff' }} />
                  {t('salarySimulation.aiSuggesting')}
                </>
              ) : (
                t('salarySimulation.getAISalary')
              )}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 4 }}>
              {error}
            </Alert>
          )}

          {salaryData && (
            <Box mt={6}>
              <Box textAlign="center" mb={3}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  {t('salarySimulation.results')}
                </Typography>
                {salaryData.aiGenerated && showText && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faRobot}
                      style={{ color: '#A4ADFF', fontSize: '20px' }}
                    />
                    <Typography
                      variant="body2"
                      color="primary.main"
                      sx={{ fontWeight: 'medium' }}
                    >
                      {t('salarySimulation.aiPowered')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Grid container spacing={3}>
                {/* MIN */}
                <Grid item xs={12} md={4}>
                  <SalaryCard
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      background: '#f5f7ff',
                      border: '1px solid #e0e4ff',
                      textAlign: 'center',
                    }}
                    onClick={() =>
                      navigate(
                        '/offres-emploi?titre=' +
                        formData.jobTitle +
                        '&ville=' +
                        formData.city
                      )
                    }
                  >
                    <Typography variant="body2" mb={1}>
                      {t('salarySimulation.min')}
                    </Typography>

                    <Typography variant="h4" fontWeight="bold" color="#5C6CFA">
                      {salaryData.currency} {salaryData.min.toLocaleString()}
                    </Typography>
                  </SalaryCard>
                </Grid>

                {/* AVERAGE (highlight) */}
                <Grid item xs={12} md={4}>
                  <SalaryCard
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      textAlign: 'center',
                      background:
                        'linear-gradient(135deg, #A4ADFF 0%, #7F8CFF 100%)',
                      color: '#fff',
                      transform: 'scale(1.05)',
                      boxShadow: '0 10px 30px rgba(164,173,255,0.4)',
                      position: 'relative',
                    }}
                    onClick={() =>
                      navigate(
                        '/offres-emploi?titre=' +
                        formData.jobTitle +
                        '&ville=' +
                        formData.city
                      )
                    }
                  >
                    {/* Badge */}
                    {/* <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fff',
                        color: '#5C6CFA',
                        px: 2,
                        py: 0.5,
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      }}
                    >
                      ⭐ Recommandé
                    </Box> */}

                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      {t('salarySimulation.average')}
                    </Typography>

                    <Typography variant="h3" fontWeight="bold">
                      {salaryData.currency}{' '}
                      {salaryData.average.toLocaleString()}
                    </Typography>
                  </SalaryCard>
                </Grid>

                {/* MAX */}
                <Grid item xs={12} md={4}>
                  <SalaryCard
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      background: '#f8faff',
                      border: '1px solid #e0e4ff',
                      textAlign: 'center',
                    }}
                    onClick={() =>
                      navigate(
                        '/offres-emploi?titre=' +
                        formData.jobTitle +
                        '&ville=' +
                        formData.city
                      )
                    }
                  >
                    <Typography variant="body2" mb={1}>
                      {t('salarySimulation.max')}
                    </Typography>

                    <Typography variant="h4" fontWeight="bold" color="#7F8CFF">
                      {salaryData.currency} {salaryData.max.toLocaleString()}
                    </Typography>
                  </SalaryCard>
                </Grid>
              </Grid>
              <Typography
                variant="caption"
                textAlign="center"
                display="block"
                mt={4}
              >
                {t('salarySimulation.disclaimer')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  )
}
